import AddressSearchEmptyState from '@/experiences/sales-portal/components/address-search-input/address-search-empty-state';
import AddressSearchErrorState from '@/experiences/sales-portal/components/address-search-input/address-search-error-state';
import AddressSearchLoadingState from '@/experiences/sales-portal/components/address-search-input/address-search-loading-state';
import i18n from '@/utils/i18n';
import MapPin from '@assets/icons/map-pin-outline.svg';
import React, { useCallback, useState } from 'react';
import { AddressSearchResult } from '@/experiences/sales-portal/components/address-search-input/use-google-api-address-search-loader';
import { createStyles } from '@/contexts/theme-context';
import {
  FlatList,
  LayoutChangeEvent,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  View,
} from 'react-native';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { List, Popover } from '@usepepper/sprout-kit-ui';
import { SearchInput } from '@/experiences/sales-portal/components/search-input';

const PIN_ICON_SIZE = 16;
const POPOVER_MAX_HEIGHT = 300;

interface Props {
  addressResults: AddressSearchResult[];
  hasError: boolean;
  isSearching: boolean;
  lastSearched: string;
  onClear: () => void;
  onRetry: () => void;
  onSearch: (text: string) => void;
  onSelectPlace?: (result: AddressSearchResult) => void;
}

export const LocationSearchInput: React.FC<Props> = ({
  addressResults,
  hasError,
  isSearching,
  lastSearched,
  onClear,
  onRetry,
  onSearch,
  onSelectPlace,
}) => {
  const [styles, theme] = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>();

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        onClear();
      }
    },
    [onClear],
  );

  const handleSearch = useCallback(
    (text: string) => {
      setQuery(text);
      if (!text.trim()) {
        // Clearing the input shouldn't trigger a search.
        onClear();
        setIsOpen(false);
        return;
      }
      onSearch(text);
      if (!isOpen) {
        setIsOpen(true);
      }
    },
    [isOpen, onClear, onSearch],
  );

  const handleSelect = useCallback(
    (result: AddressSearchResult) => {
      // Reflect the selected place in the input
      setQuery(result.label);
      onSelectPlace?.(result);
      setIsOpen(false);
      onClear();
    },
    [onClear, onSelectPlace],
  );

  const handleAnchorLayout = useCallback((event: LayoutChangeEvent) => {
    setAnchorWidth(event.nativeEvent.layout.width);
  }, []);

  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (hasError && e.nativeEvent.key === Key.Enter) {
        e.preventDefault();
        onRetry();
      }
    },
    [hasError, onRetry],
  );

  const showError = !isSearching && hasError;
  const showNoResults =
    !isSearching &&
    !hasError &&
    lastSearched.length > 0 &&
    addressResults.length === 0;
  const showDropdown =
    isSearching || showError || addressResults.length > 0 || showNoResults;

  return (
    <View style={styles.container}>
      <Popover onOpenChange={handleOpenChange} open={isOpen && showDropdown}>
        <Popover.Anchor asChild>
          <View onLayout={handleAnchorLayout}>
            <SearchInput
              onFocus={() => setIsOpen(true)}
              onKeyPress={handleKeyPress}
              onSearchChange={handleSearch}
              placeholderText={i18n.t(
                'sales_portal.prospect_finder.location_search.placeholder',
              )}
              searchText={query}
            />
          </View>
        </Popover.Anchor>
        <Popover.Content style={styles.popoverContainer}>
          {isSearching ? (
            <AddressSearchLoadingState
              style={anchorWidth ? { width: anchorWidth } : undefined}
            />
          ) : showError ? (
            <AddressSearchErrorState
              style={anchorWidth ? { width: anchorWidth } : undefined}
            />
          ) : (
            <FlatList
              data={addressResults}
              keyExtractor={(item) => item.key}
              ListEmptyComponent={
                showNoResults ? <AddressSearchEmptyState /> : null
              }
              renderItem={({ item }) => (
                <List.Item
                  onPress={() => handleSelect(item)}
                  pressable
                  style={styles.resultRow}
                >
                  <List.Item.Leading>
                    <MapPin
                      height={PIN_ICON_SIZE}
                      stroke={theme.sk.color.icon.tertiary}
                      width={PIN_ICON_SIZE}
                    />
                  </List.Item.Leading>
                  <List.Item.Content>
                    <List.Item.Title fontWeight="$medium" numberOfLines={1}>
                      {item.label}
                    </List.Item.Title>
                    <List.Item.Subtitle
                      color="$textSecondary"
                      numberOfLines={1}
                    >
                      {item.description}
                    </List.Item.Subtitle>
                  </List.Item.Content>
                </List.Item>
              )}
              style={[styles.list, anchorWidth ? { width: anchorWidth } : null]}
            />
          )}
        </Popover.Content>
      </Popover>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  list: {
    flex: 1,
    scrollbarColor: `${theme.sk.color.bg.scrollbar} transparent`,
    scrollbarWidth: 'thin',
  },
  popoverContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    boxShadow: '0 12px 24px 0 rgba(0, 0, 0, 0.10)',
    maxHeight: POPOVER_MAX_HEIGHT,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  resultRow: {
    padding: theme.sk.spacing.spaceSmall,
  },
}));
